import { CeruleanButton } from 'components/Button'
import I18n from 'components/I18n'
import { OverlayLoader } from 'components/Loaders'
import { fiatSymbols } from 'constants/currency'
import { actions, selectors } from 'data'
import { Bank, YapilyData } from 'data/Payments/BankTransfer/types'
import { StateType } from 'data/types'
import messages from 'lang'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import BeneficiaryButton from 'scenes/Cashflow/BeneficiaryButton'
import styled from 'styled-components'

import AmountInput from '../AmountInput'
import Plaid from '../LinkBank/Plaid'
import Agreement from '../LinkBank/Yapily/Agreement'
import { Header, ModalCard, ModalTitle } from '../styles'
import BankSettlement from './BankSettlement'
import Payment from './Payment'
import Instructions from './WireTransfer/Instructions'

const Body = styled.main`
  padding: 1rem 0 2.5rem 0;
`

const Footer = styled.footer`
  display: flex;

  > * {
    flex: 1;
  }
`

const getError = (amount, limits, minorUnits) => {
  if (!amount || !limits) return undefined
  const min = Number(limits.min) / minorUnits
  const max = Number(limits.max) / minorUnits
  const current = Number(amount)

  if (current < min) {
    return (
      <I18n
        data={messages.validation_min_amount}
        field={<I18n data={messages.amount} />}
        minAmount={min}
      />
    )
  }

  if (current > max) {
    return (
      <I18n
        data={messages.validation_max_amount}
        field={<I18n data={messages.amount} />}
        maxAmount={max}
      />
    )
  }
}

type Props = {
  bank: Bank
  currency: string
  onSelectMethod: () => void
}
const DepositFormModal = ({ bank, currency, onSelectMethod }: Props) => {
  const accounts = useSelector(selectors.payments.getAccounts)
  const dispatch = useDispatch()
  const deposit = useSelector(selectors.payments.submitDeposit.getSubmittedDeposit)
  const bankSettlement = useSelector(selectors.payments.bankTransfer.getBankSettlement)
  const [pristine, setPristine] = useState(true)

  const eligibility = useSelector((state: StateType) =>
    selectors.payments.eligible.getEligibility(state)(currency)
  )
  const minorUnits = useSelector((state: StateType) =>
    selectors.currencies.getMinorUnitsFactory(state)(currency)
  )
  const config = useSelector(selectors.config.getConfig)
  const isSubmitting = useSelector(selectors.payments.submitDeposit.isSubmitting)

  const [amount, setAmount] = useState<string>('')
  const [refresh, setRefresh] = useState(false)
  const [innerBank, setInnerBank] = useState<Bank>(bank)

  const [showAgreement, setShowAgreement] = useState(false)

  useEffect(() => {
    dispatch(actions.payments.elegible.fetchElegiblePMs(currency))
    dispatch(actions.payments.fetchAccount(currency))
  }, [currency])

  if (!eligibility) return <OverlayLoader />

  if (!accounts || !accounts[currency] || isSubmitting) {
    return <OverlayLoader />
  }

  const submitDeposit = (amount, pm) => {
    dispatch(
      actions.analytics.trackEvent({
        key: 'Deposit Submitted',
        properties: {}
      })
    )
    if (config.settlementFlow) {
      dispatch(
        actions.payments.fetchBankSettlement(
          {
            settlementRequest: {
              amount,
              product: 'MERCURY'
            }
          },
          pm
        )
      )
    } else {
      dispatch(actions.payments.submitDeposit.submitDeposit(amount, pm.currency, pm))
    }
  }

  const bankEligibility = eligibility.find((e) => e.type === 'BANK_TRANSFER')

  if (!bankEligibility) {
    const bankData = accounts[currency]?.value
    if (!bankData) return null

    return (
      <ModalCard>
        <Header>
          <ModalTitle>
            <I18n data={messages.deposit} />
          </ModalTitle>
        </Header>
        <Instructions content={bankData.content} />
      </ModalCard>
    )
  }

  if (bankSettlement)
    return (
      <BankSettlement
        settlementResponse={bankSettlement.settlementResponse}
        message={bankSettlement.message}
        bank={bankSettlement.bank}
        setRefresh={setRefresh}
      />
    )

  const handleClose = () => {
    dispatch(actions.payments.submitDeposit.clear())
    dispatch(actions.app.hideModal('DEPOSIT'))
  }

  if (deposit)
    return (
      <ModalCard>
        <Payment id={deposit.paymentId} onClose={handleClose} />
      </ModalCard>
    )

  if (refresh) {
    return (
      <Plaid
        refresh
        onBack={() => setRefresh(false)}
        onSuccess={(bankUpdated) => {
          setInnerBank(bankUpdated)
          setRefresh(false)
        }}
      />
    )
  }

  const amountError = getError(amount, bankEligibility.limits, minorUnits)

  const handleClick = () => {
    if (!bank || !amount || amountError) {
      setPristine(false)
      return
    }

    if (bank?.partner === 'YAPILY') {
      setShowAgreement(true)
      return
    }

    submitDeposit(amount, bank)
  }

  if (showAgreement) {
    return (
      <ModalCard>
        <Agreement
          entity={(bank.attributes as YapilyData).entity}
          onAccept={() => {
            setShowAgreement(false)
            submitDeposit(amount, bank)
          }}
          onDecline={() => {
            setShowAgreement(false)
          }}
        />
      </ModalCard>
    )
  }

  return (
    <ModalCard>
      <Header>
        <ModalTitle>
          <I18n data={messages.deposit} />
        </ModalTitle>
      </Header>
      <Body>
        <AmountInput
          e2e='depositAmount'
          error={amountError}
          onChange={setAmount}
          symbol={fiatSymbols[currency] ?? currency}
          value={amount}
        />
        <BeneficiaryButton
          bank={innerBank}
          e2e='depositModalSelectDepositMethodButton'
          hasError={!bank && !pristine}
          onClick={onSelectMethod}
          title={<I18n data={messages.select_deposit_method} />}
        />
      </Body>
      <Footer>
        <CeruleanButton onClick={handleClick}>
          <I18n data={messages.button_continue} />
        </CeruleanButton>
      </Footer>
    </ModalCard>
  )
}

export default DepositFormModal
